import { useCallback, useEffect, useMemo, useState } from 'react';
import { AbilityProvider } from '@casl/react';
import { getEntityStoreByName, getStoreByName } from '@datorama/akita';
import { IntlErrorCode } from '@formatjs/intl';
import { shouldPolyfill as shouldPolyfillDateTime } from '@formatjs/intl-datetimeformat/should-polyfill.js';
import { shouldPolyfill as shouldPolyfillDisplayNames } from '@formatjs/intl-displaynames/should-polyfill.js';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import * as muiLocales from '@mui/material/locale';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as muiChartLocales from '@mui/x-charts-premium/locales';
import * as muiDataGridLocales from '@mui/x-data-grid-premium/locales';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { flatten } from 'flat';
import { getUserLocale } from 'get-user-locale';
import { merge } from 'lodash-es';
import { closeSnackbar, type SnackbarKey, SnackbarProvider } from 'notistack';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { createBrowserRouter, RouterProvider } from 'react-router';
import ability from 'casl/ability';
import ThemedSnackbar from 'components/Snackbar/ThemeWrapper';
import FullPageLoadingIndicator from 'components/v2/FullPageLoadingIndicator';
import ScrollToHashElement from 'components/v2/ScrollToHashElement';
import ApplicationContextComponent from 'context/ApplicationContext';
import useApplicationContext from 'context/ApplicationContext/hook';
import LocaleContextComponent from 'context/LocaleContext';
import { OrganisationContextComponent } from 'context/OrganisationContext';
import useOrganisationContext from 'context/OrganisationContext/hook';
import { PeriodContextComponent } from 'context/PeriodContext';
import useUserContext from 'context/UserContext/hook';
import generateRoutes from 'routes';
import useDesignFacade from 'state/Design/hook';
import envoriaTheme from 'themes/envoriaTheme';

// The force polyfills replace these properties before their locale data loads, so a failed load has to put the originals back.
function captureProperties(target: object, names: string[]) {
   const descriptors = names.map((name) => [name, Object.getOwnPropertyDescriptor(target, name)] as const);

   return () => {
      descriptors.forEach(([name, descriptor]) => {
         if (descriptor) {
            Object.defineProperty(target, name, descriptor);
         } else {
            Reflect.deleteProperty(target, name);
         }
      });
   };
}

// Module load and the language effect both call the loaders below. Running the calls one after another makes each capture what the
// previous call left installed, so a failed call never restores a polyfill whose locale data didn't load.
function runInOrder(load: (locale: string) => Promise<void>) {
   let queue = Promise.resolve();

   return async (locale: string) => {
      const run = queue.then(async () => load(locale));
      queue = run.catch(() => undefined);
      return run;
   };
}

const polyfillDateTimeFormat = runInOrder(async (locale) => {
   const unsupportedLocale = shouldPolyfillDateTime(locale);
   // This locale is supported
   if (!unsupportedLocale) {
      return;
   }
   const restoreIntl = captureProperties(Intl, ['DateTimeFormat']);
   const restoreDate = captureProperties(Date.prototype, ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString']);
   try {
      // Load the polyfill 1st BEFORE loading data
      await import('@formatjs/intl-datetimeformat/polyfill-force.js');

      // Parallelize CLDR data loading
      const dataPolyfills = [
         import('@formatjs/intl-datetimeformat/add-all-tz.js'),
         import(`@formatjs/intl-datetimeformat/locale-data/${unsupportedLocale}.js`),
      ];
      await Promise.all(dataPolyfills);
   } catch {
      // A failed chunk load keeps the browser's own Intl.DateTimeFormat in use.
      restoreIntl();
      restoreDate();
   }
});

const polyfillDisplayNames = runInOrder(async (locale) => {
   const unsupportedLocale = shouldPolyfillDisplayNames(locale);
   // This locale is supported
   if (!unsupportedLocale) {
      return;
   }
   const restoreIntl = captureProperties(Intl, ['DisplayNames']);
   try {
      // Load the polyfill 1st BEFORE loading data
      await import('@formatjs/intl-displaynames/polyfill-force.js');
      await import(`@formatjs/intl-displaynames/locale-data/${unsupportedLocale}.js`);
   } catch {
      // A failed chunk load keeps the browser's own Intl.DisplayNames in use.
      restoreIntl();
   }
});

const defaultLocale = 'en-US';

let languageWithoutRegionCode = getUserLocale().substring(0, 2);

try {
   require.resolve(`lang/${languageWithoutRegionCode}.json`);
} catch {
   languageWithoutRegionCode = defaultLocale.substring(0, 2);
}

axios.defaults.withCredentials = true;
axios.defaults.headers.common['Accept-Language'] = getUserLocale();

void polyfillDateTimeFormat(languageWithoutRegionCode);
void polyfillDisplayNames(languageWithoutRegionCode);

function App() {
   const {
      state: { locale: userLocale, language },
   } = useUserContext();
   const {
      state: { design, isLoading: isDesignLoading },
   } = useDesignFacade();
   const {
      state: { customization, theme },
   } = useOrganisationContext();
   const {
      state: { isLoading: isApplicationContextLoading },
   } = useApplicationContext();

   const [messages, setMessages] = useState({});
   const [locale, setLocale] = useState(defaultLocale);

   useEffect(() => {
      async function loadMessages(localeOrLanguageCode: string) {
         let tempMessages = {};
         let returnLanguage;

         try {
            require.resolve(`lang/${localeOrLanguageCode.substring(0, 2)}.json`);
            tempMessages = await import(`lang/${localeOrLanguageCode.substring(0, 2)}.json`);
            returnLanguage = localeOrLanguageCode;
         } catch {
            tempMessages = await import(`lang/${defaultLocale.substring(0, 2)}.json`);
            returnLanguage = defaultLocale;
         }

         const customMessages = customization?.messages?.[language ? locale.substring(0, 2) : languageWithoutRegionCode] ?? {};

         setMessages(merge(tempMessages, customMessages));
         setLocale(returnLanguage);

         return returnLanguage;
      }

      if (userLocale) {
         loadMessages(userLocale)
            .then((lang_) => {
               axios.defaults.headers.common['Accept-Language'] = lang_;
               document.documentElement.lang = lang_;
               return undefined;
            })
            .catch(() => {
               // do nothing
            });
      } else {
         loadMessages(languageWithoutRegionCode)
            .then((lang_) => {
               axios.defaults.headers.common['Accept-Language'] = lang_;
               document.documentElement.lang = lang_;
               return undefined;
            })
            .catch(() => {
               // do nothing
            });
      }
   }, [language, locale, customization?.messages, userLocale]);

   // Update polyfill data when language is changed
   useEffect(() => {
      const activeLang = (userLocale ?? languageWithoutRegionCode).substring(0, 2);
      void polyfillDisplayNames(activeLang);
      void polyfillDateTimeFormat(activeLang);
   }, [userLocale]);

   const onClickDismiss = useCallback((key: SnackbarKey) => {
      if (key) {
         closeSnackbar(key);
      }
      if ((key ?? '').toString().includes('#')) {
         const storeName = key.toString().substring(0, key.toString().indexOf('#'));

         if (storeName) {
            const store = getEntityStoreByName(storeName) ?? getStoreByName(storeName);

            if (store) {
               // @ts-expect-error - store not typed?
               const currentError = store.getValue()?.error;
               if (currentError?.key === key) {
                  store.setError(null);
               }
            }
         }
      }
   }, []);

   const snackbarAction = useCallback(
      (key: SnackbarKey) => (
         <Button onClick={() => onClickDismiss(key)} sx={{ textTransform: 'uppercase', color: 'currentColor' }}>
            <FormattedMessage id="snackbars.dismiss" defaultMessage="Dismiss" />
         </Button>
      ),
      [onClickDismiss]
   );

   const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
   const mode = prefersDarkMode ? 'dark' : 'light';

   const modeTheme = useMemo(
      () =>
         createTheme(
            envoriaTheme(mode, theme ?? design?.theme),

            userLocale && locale && muiLocales && Object.prototype.hasOwnProperty.call(muiLocales, locale.replace('-', ''))
               ? muiLocales[locale.replace('-', '') as keyof typeof muiLocales]
               : muiLocales.enUS,

            userLocale && locale && muiDataGridLocales && Object.prototype.hasOwnProperty.call(muiDataGridLocales, locale.replace('-', ''))
               ? muiDataGridLocales[locale.replace('-', '') as keyof typeof muiDataGridLocales]
               : muiDataGridLocales.enUS,

            userLocale && locale && muiChartLocales && Object.prototype.hasOwnProperty.call(muiChartLocales, locale.replace('-', ''))
               ? muiChartLocales[locale.replace('-', '') as keyof typeof muiChartLocales]
               : muiChartLocales.enUS
         ),
      [mode, theme, userLocale, design?.theme, locale]
   );

   const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

   const router = sentryCreateBrowserRouter(generateRoutes());

   const [showLoadingText, setShowLoadingText] = useState(false);

   useEffect(() => {
      if (/.demo|dev.envoria.app/.test(window.location.host)) {
         setTimeout(() => {
            setShowLoadingText(true);
         }, 4000);
      }
   }, []);

   if (isDesignLoading !== false || isApplicationContextLoading !== false) {
      return (
         <>
            <FullPageLoadingIndicator invisible />
            {showLoadingText && (
               <Typography
                  sx={{
                     textAlign: 'center',
                     margin: 'auto',
                     position: 'absolute',
                     top: '55vh',
                     width: '100%',
                     zIndex: 1302,
                  }}
               >
                  Instance is starting. This might take up to 90 seconds. This page will reload automatically.
               </Typography>
            )}
         </>
      );
   }

   return (
      <>
         <Helmet defaultTitle="envoria">
            <title>{design?.title}</title>
         </Helmet>
         <LocaleContextComponent>
            <ApplicationContextComponent>
               <OrganisationContextComponent>
                  <PeriodContextComponent>
                     <IntlProvider
                        locale={userLocale ?? languageWithoutRegionCode}
                        messages={flatten(messages)}
                        defaultLocale={defaultLocale.substring(0, 2)}
                        onError={
                           process.env.REACT_APP_WARN_DEV_ERRORS === 'true'
                              ? (error) => {
                                   if (error.code === IntlErrorCode.MISSING_TRANSLATION) {
                                      return;
                                   }
                                   console.error(error);
                                }
                              : undefined
                        }
                     >
                        <StyledEngineProvider injectFirst>
                           <ThemeProvider theme={modeTheme}>
                              <SnackbarProvider
                                 dense
                                 maxSnack={3}
                                 preventDuplicate
                                 action={snackbarAction}
                                 autoHideDuration={5000}
                                 Components={{
                                    default: ThemedSnackbar,
                                    success: ThemedSnackbar,
                                    error: ThemedSnackbar,
                                    warning: ThemedSnackbar,
                                    info: ThemedSnackbar,
                                 }}
                              >
                                 <AbilityProvider value={ability}>
                                    <ScrollToHashElement />
                                    <CssBaseline />
                                    <RouterProvider router={router} />
                                 </AbilityProvider>
                              </SnackbarProvider>
                           </ThemeProvider>
                        </StyledEngineProvider>
                     </IntlProvider>
                  </PeriodContextComponent>
               </OrganisationContextComponent>
            </ApplicationContextComponent>
         </LocaleContextComponent>
      </>
   );
}

export default App;
